import { useMemo } from "react";

/**
 * LessonVideo
 * Renders an embedded player given:
 *  (a) a YouTube URL in any of these forms:
 *      - https://youtu.be/ID
 *      - https://www.youtube.com/watch?v=ID
 *      - https://www.youtube.com/shorts/ID
 *      - https://www.youtube.com/embed/ID
 *  (b) a direct video file URL ending in .mp4 / .webm / .mov / .m4v (case-insensitive,
 *      querystring stripped).
 * Otherwise falls back to a static placeholder.
 */
function getYouTubeId(url) {
  if (!url || typeof url !== "string") return null;
  try {
    const u = new URL(url.trim());
    if (u.hostname === "youtu.be") return u.pathname.replace("/", "").split("?")[0] || null;
    if (u.hostname.endsWith("youtube.com") || u.hostname.endsWith("youtube-nocookie.com")) {
      if (u.searchParams.get("v")) return u.searchParams.get("v");
      const parts = u.pathname.split("/").filter(Boolean);
      if (parts[0] === "shorts" && parts[1]) return parts[1];
      if (parts[0] === "embed" && parts[1]) return parts[1];
    }
  } catch (e) { /* not a valid URL */ }
  return null;
}

function isDirectVideoUrl(url) {
  if (!url || typeof url !== "string") return false;
  try {
    const u = new URL(url.trim());
    const path = u.pathname.toLowerCase();
    return /\.(mp4|webm|mov|m4v)$/.test(path);
  } catch (e) {
    return false;
  }
}

function mimeFor(url) {
  const u = url.toLowerCase();
  if (u.includes(".webm")) return "video/webm";
  if (u.includes(".mov") || u.includes(".m4v")) return "video/quicktime";
  return "video/mp4";
}

function LessonVideo({ url, title, placeholder, className = "" }) {
  const ytId = useMemo(() => getYouTubeId(url), [url]);
  const isDirect = useMemo(() => !ytId && isDirectVideoUrl(url), [ytId, url]);

  if (ytId) {
    const src = `https://www.youtube-nocookie.com/embed/${ytId}?rel=0&modestbranding=1`;
    return (
      <div
        className={`relative w-full overflow-hidden rounded-xl border-2 border-blue-200 bg-black shadow-md ${className}`}
        style={{ paddingTop: "56.25%" }}  // 16:9
        data-testid="lesson-video"
      >
        <iframe
          src={src}
          title={title || "Lesson video"}
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
        />
      </div>
    );
  }

  if (isDirect) {
    return (
      <div
        className={`relative w-full overflow-hidden rounded-xl border-2 border-blue-200 bg-black shadow-md ${className}`}
        data-testid="lesson-video"
      >
        <video
          controls
          playsInline
          preload="metadata"
          className="w-full h-auto block"
          title={title || "Lesson video"}
        >
          <source src={url} type={mimeFor(url)} />
          Your browser doesn't support embedded video.
        </video>
      </div>
    );
  }

  // Fallback placeholder
  if (placeholder) {
    return (
      <div
        className={`bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl p-6 text-center border-2 border-dashed border-blue-300 ${className}`}
        data-testid="lesson-video-placeholder"
      >
        <div className="text-4xl mb-2">🎥</div>
        <div className="text-sm font-semibold text-blue-700">Video coming soon</div>
        <div className="text-xs text-blue-600 mt-1">{placeholder}</div>
      </div>
    );
  }
  return null;
}

export default LessonVideo;
